/**
 * Language state (§30).
 *
 * The rules this implements, in the spec's own words:
 *
 *   "Saved preference is default."
 *   "Explicit selection overrides detection."
 *   "Do not permanently infer preference from one message."
 *
 * So there are two layers. The *persisted* preference comes from the student
 * profile and only changes when the child explicitly picks a language. The
 * *session* language is what the UI is using right now, which a child can
 * flip without that becoming their permanent setting.
 *
 * That distinction matters: a Hinglish-speaking child who switches to English
 * to read one thing should not find their whole app in English tomorrow.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  DEFAULT_LANGUAGE,
  isLanguage,
  translate,
  type Language,
  type MessageKey,
} from '@/i18n/messages';

const SESSION_KEY = 'preplift.language.session';

interface LanguageContextValue {
  language: Language;
  /** The profile's saved preference, or null before the profile loads. */
  preferred: Language | null;
  /** Change for this session only. Does not touch the profile. */
  setSessionLanguage(next: Language): void;
  /** Adopt the profile's preference once it arrives. */
  adoptPreference(next: Language): void;
  t(key: MessageKey, values?: Record<string, string | number>): string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readSessionLanguage(): Language | null {
  try {
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [preferred, setPreferred] = useState<Language | null>(null);
  const [sessionLanguage, setSession] = useState<Language | null>(() => readSessionLanguage());

  // Session choice wins, then the saved preference, then English.
  const language: Language = sessionLanguage ?? preferred ?? DEFAULT_LANGUAGE;

  const setSessionLanguage = useCallback((next: Language) => {
    setSession(next);
    try {
      // sessionStorage, not localStorage: a session override is meant to end
      // with the session. §56 also asks that temporary state stay temporary.
      window.sessionStorage.setItem(SESSION_KEY, next);
    } catch {
      /* private mode — the in-memory value still applies */
    }
  }, []);

  const adoptPreference = useCallback((next: Language) => {
    setPreferred(next);
  }, []);

  // Keep the document language accurate: screen readers use it to pick a
  // voice, and Hinglish read with an English synthesiser is unintelligible.
  useEffect(() => {
    // Hinglish is Hindi written in Roman script; `hi-Latn` is the correct BCP-47
    // tag for exactly that, and is what lets a screen reader do the right thing.
    document.documentElement.lang = language === 'hinglish' ? 'hi-Latn' : 'en-IN';
  }, [language]);

  const t = useCallback(
    (key: MessageKey, values?: Record<string, string | number>) =>
      translate(key, language, values),
    [language],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ language, preferred, setSessionLanguage, adoptPreference, t }),
    [language, preferred, setSessionLanguage, adoptPreference, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return context;
}

/** Shorthand for components that only need the translator. */
export function useT(): LanguageContextValue['t'] {
  return useLanguage().t;
}
